"use client";

import React, { useState, useEffect, useRef, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useTheme } from '../contexts/ThemeContext';

// MUI Components - targeted imports for better performance
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';

// Icons - targeted imports
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';

// Navigation component
import Navigation from './Navigation';

// Editor panels and action buttons
import EditorPanel from './EditorPanel';
import ActionButtons from './ActionButtons';
import ModeTabs, { EDITOR_MODES, type EditorMode } from './ModeTabs';

// View transitions
import { CSSTransition } from 'react-transition-group';

// Utilities
import { encodeBase64, decodeBase64 } from '../utils/base64';
import {
  applyPatchToJson,
  generateJsonPatch,
  getVisualDiff,
  verifyPatch,
  type JsonPatchFailure,
} from '../utils/jsonPatch';

// Lazy load non-critical components
const ExpandDialog = dynamic(() => import('./ExpandDialog'), { ssr: false });
const Analytics = dynamic(() => import('@vercel/analytics/react').then(mod => mod.Analytics as any), { ssr: false });
const SpeedInsights = dynamic(() => import('@vercel/speed-insights/next').then(mod => mod.SpeedInsights as any), { ssr: false });
const AnalyticsTracker = dynamic(() => import('./Analytics'), { ssr: false });

const Features = dynamic(() => import('./Features'), {
  loading: () => <div style={{ height: '400px', backgroundColor: 'var(--bg)', borderRadius: 'var(--radius-lg)' }} />
});
const LearningSection = dynamic(() => import('./LearningSection'), {
  loading: () => <div style={{ height: '500px', backgroundColor: 'var(--bg)', borderRadius: 'var(--radius-lg)' }} />
});
const FAQ = dynamic(() => import('./FAQ'), {
  loading: () => <div style={{ height: '600px', backgroundColor: 'var(--bg)', borderRadius: 'var(--radius-lg)' }} />
});

// Lazy-loaded mode views - the apply and verify bundles only download when that tab is opened
const ApplyView = dynamic(() => import('./modes/ApplyView'));
const VerifyView = dynamic(() => import('./modes/VerifyView'));

type ExpandedEditorType = 'old' | 'new' | 'patch' | null;
type PanelIconComponent = typeof ContentCopyIcon;

const SlideTransition = React.forwardRef(function SlideTransition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface EditorProps {
  // Optional overrides let other pages (e.g. /diff) reuse the same tool with
  // their own hero and follow-up content instead of the homepage sections.
  hero?: React.ReactNode;
  children?: React.ReactNode;
  activePage?: 'home' | 'docs' | 'examples' | 'operations' | 'diff';
}

function Editor({ hero, children, activePage = 'home' }: EditorProps) {
  const { isDarkMode } = useTheme();

  // JSON states
  const [oldJson, setOldJson] = useState('');
  const [newJson, setNewJson] = useState('');
  const [patchResult, setPatchResult] = useState('');
  const [appliedResult, setAppliedResult] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [failureDetail, setFailureDetail] = useState<JsonPatchFailure | null>(null);
  const patchResultRef = useRef<HTMLDivElement>(null);
  const appliedResultRef = useRef<HTMLDivElement>(null);
  const verifyOpsRef = useRef<HTMLDivElement>(null);

  // Active workflow mode (Generate / Apply / Verify)
  const [mode, setMode] = useState<EditorMode>('generate');
  const isDiffPage = activePage === 'diff';
  const activeMode: EditorMode = isDiffPage ? 'generate' : mode;
  const visibleModes: readonly EditorMode[] = isDiffPage ? ['generate'] : EDITOR_MODES;
  const [slideDir, setSlideDir] = useState<'forward' | 'back'>('forward');
  const generateViewRef = useRef<HTMLElement | undefined>(undefined);
  const applyViewRef = useRef<HTMLElement | undefined>(undefined);
  const verifyViewRef = useRef<HTMLElement | undefined>(undefined);
  const modeNodeRefs: Record<EditorMode, React.MutableRefObject<HTMLElement | undefined>> = {
    generate: generateViewRef,
    apply: applyViewRef,
    verify: verifyViewRef
  };

  // Snackbar state
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [showRawDiffPatch, setShowRawDiffPatch] = useState(false);

  // Expanded editor state
  const [expandedEditor, setExpandedEditor] = useState<ExpandedEditorType>(null);

  // URL state tracking
  const [urlSearch, setUrlSearch] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setUrlSearch(window.location.search);
      const handleUrlChange = () => setUrlSearch(window.location.search);
      window.addEventListener('popstate', handleUrlChange);
      return () => window.removeEventListener('popstate', handleUrlChange);
    }
  }, []);

  // Sync from URL
  useEffect(() => {
    if (typeof window !== 'undefined' && urlSearch) {
      const queryParams = new URLSearchParams(urlSearch);
      const hasParams = queryParams.has('old') || queryParams.has('new') || queryParams.has('patch');

      if (hasParams) {
        // Explicit mode param wins. Without one, infer from content: current
        // share links always carry old/new/patch (possibly empty) and omit
        // `mode` only in generate mode; older apply links carry patch w/o new.
        const modeParam = queryParams.get('mode');
        if (modeParam && EDITOR_MODES.includes(modeParam as EditorMode)) {
          setMode(modeParam as EditorMode);
        } else {
          let decodedNew = '';
          let decodedPatch = '';
          try { decodedNew = decodeBase64(queryParams.get('new') || '').trim(); } catch (e) { }
          try { decodedPatch = decodeBase64(queryParams.get('patch') || '').trim(); } catch (e) { }
          if (decodedPatch && !decodedNew) {
            setMode('apply');
          }
        }
        if (queryParams.has('old')) {
          try { setOldJson(decodeBase64(queryParams.get('old') || '')); } catch (e) { }
        }
        if (queryParams.has('new')) {
          try { setNewJson(decodeBase64(queryParams.get('new') || '')); } catch (e) { }
        }
        if (queryParams.has('patch')) {
          try { setPatchResult(decodeBase64(queryParams.get('patch') || '')); } catch (e) { }
        }
        if (queryParams.has('result')) {
          try { setAppliedResult(decodeBase64(queryParams.get('result') || '')); } catch (e) { }
        }
      }
    }
  }, [urlSearch]);

  // Sync to URL - debounced so typing does not rewrite history on every
  // keystroke (closes the URL-sync staff-review flag from PR #35).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!(oldJson || newJson || patchResult || appliedResult || activeMode !== 'generate')) return;

    const timer = setTimeout(() => {
      const qp = new URLSearchParams();
      qp.set('old', encodeBase64(oldJson));
      qp.set('new', encodeBase64(newJson));
      qp.set('patch', encodeBase64(patchResult));
      if (activeMode !== 'generate') {
        qp.set('mode', activeMode);
      }
      if (appliedResult) {
        qp.set('result', encodeBase64(appliedResult));
      }
      window.history.replaceState(null, '', `?${qp.toString()}`);
    }, 400);

    return () => clearTimeout(timer);
  }, [oldJson, newJson, patchResult, appliedResult, activeMode]);

  const visualDiff = useMemo(() => {
    if (!patchResult.trim()) return null;
    return getVisualDiff(patchResult, oldJson);
  }, [patchResult, oldJson]);

  const setFailure = (failure: JsonPatchFailure) => {
    const prefix = typeof failure.operationIndex === 'number'
      ? `Operation ${failure.operationIndex + 1}: `
      : '';

    setStatusMessage('');
    setErrorMessage(`${prefix}${failure.message}`);
    setFailureDetail(failure);
  };

  const clearFeedback = () => {
    setStatusMessage('');
    setErrorMessage('');
    setFailureDetail(null);
  };

  const updateOldJson = (value: string) => {
    setOldJson(value);
    clearFeedback();
  };

  const updateNewJson = (value: string) => {
    setNewJson(value);
    clearFeedback();
  };

  const updatePatchResult = (value: string) => {
    setPatchResult(value);
    clearFeedback();
  };

  const scrollToPanel = (ref: React.RefObject<HTMLDivElement | null>) => {
    // Wait for the state-driven render to commit, then bring the section into view.
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleModeChange = (nextMode: EditorMode) => {
    if (nextMode === mode) return;
    setSlideDir(EDITOR_MODES.indexOf(nextMode) > EDITOR_MODES.indexOf(mode) ? 'forward' : 'back');
    setMode(nextMode);
    // Stale banners from another mode only confuse things ("generated..." while applying)
    clearFeedback();
  };

  // Global shortcut: Ctrl+Enter (Windows/Linux) or Cmd+Enter (Mac) runs the
  // current mode's action from anywhere on the page. Registered on window so
  // it works regardless of focus (the previous Box-scoped onKeyDown only fired
  // when focus was inside the editors). No dependency array: re-registers each
  // render so the closure always sees the current mode and JSON state.
  useEffect(() => {
    const handleWorkspaceKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        if (activeMode === 'generate') handleGeneratePatch();
        else if (activeMode === 'apply') handleApplyPatch();
        else handleVerifyPatch();
      }
    };
    window.addEventListener('keydown', handleWorkspaceKeyDown);
    return () => window.removeEventListener('keydown', handleWorkspaceKeyDown);
  });

  const handleGeneratePatch = () => {
    if (!oldJson.trim() || !newJson.trim()) {
      setSnackbarMsg('Please enter both Source and Target JSON to generate a patch');
      return;
    }

    const result = generateJsonPatch(oldJson, newJson);

    if (!result.ok) {
      setFailure(result as JsonPatchFailure);
      return;
    }

    setPatchResult(result.value);
    setAppliedResult('');
    setErrorMessage('');
    setFailureDetail(null);
    setStatusMessage(`Patch generated successfully - ${result.operationCount || 0} operations created.`);
    scrollToPanel(patchResultRef);
  };

  const handleApplyPatch = () => {
    if (!oldJson.trim() || !patchResult.trim()) {
      setSnackbarMsg('Please enter Source JSON and a JSON Patch to apply');
      return;
    }

    const result = applyPatchToJson(oldJson, patchResult);

    if (!result.ok) {
      setFailure(result as JsonPatchFailure);
      return;
    }

    setAppliedResult(result.value);
    setErrorMessage('');
    setFailureDetail(null);
    setStatusMessage(`Patch applied successfully - ${result.operationCount || 0} operations completed.`);
    scrollToPanel(appliedResultRef);
  };

  const handleVerifyPatch = () => {
    if (!oldJson.trim() || !newJson.trim() || !patchResult.trim()) {
      setSnackbarMsg('Please enter Source JSON, Target JSON, and a JSON Patch to verify');
      return;
    }

    const result = verifyPatch(oldJson, newJson, patchResult);

    if (!result.ok) {
      setFailure(result as JsonPatchFailure);
      return;
    }

    setAppliedResult(result.value);
    setErrorMessage('');
    setFailureDetail(null);
    setStatusMessage('Patch verified - result exactly matches target JSON.');
    scrollToPanel(verifyOpsRef);
  };

  const handleSwap = () => {
    setOldJson(newJson);
    setNewJson(oldJson);
    setPatchResult('');
    setAppliedResult('');
    clearFeedback();
  };

  const handlePasteIntoPatch = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (!text.trim()) {
        setSnackbarMsg('Clipboard is empty');
        return;
      }
      updatePatchResult(text);
      setSnackbarMsg('Patch pasted from clipboard');
    } catch {
      setSnackbarMsg('Clipboard access denied - paste manually with Ctrl+V');
    }
  };

  const handleFileDrop = (setter: (value: string) => void, label: string) => (text: string, fileName?: string) => {
    setter(text);
    setSnackbarMsg(`${fileName || 'File'} loaded into ${label}`);
  };

  const handleFileDropError = (fileName?: string) => {
    setSnackbarMsg(`${fileName ? `"${fileName}"` : 'That file'} is not a .json file - drop a JSON document`);
  };

  const handleUseResultAsSource = () => {
    if (!appliedResult.trim()) return;
    updateOldJson(appliedResult);
    setSnackbarMsg('Applied result moved into Source JSON');
  };

  const handleCopyPatch = async () => {
    if (!patchResult.trim()) {
      setSnackbarMsg('No valid patch to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(patchResult);
      setSnackbarMsg('Patch copied to clipboard!');
    } catch {
      setSnackbarMsg('Failed to copy patch.');
    }
  };

  const handleCopyResult = async () => {
    if (!appliedResult.trim()) {
      setSnackbarMsg('No applied result to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(appliedResult);
      setSnackbarMsg('Result copied to clipboard!');
    } catch {
      setSnackbarMsg('Failed to copy result.');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setSnackbarMsg('Link copied to clipboard!');
    } catch {
      setSnackbarMsg('Failed to copy link.');
    }
  };

  const downloadJson = (contents: string, filename: string, snackbar: string) => {
    const blob = new Blob([contents], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    setSnackbarMsg(snackbar);
  };

  const handleDownloadPatch = () => {
    if (!patchResult.trim()) {
      setSnackbarMsg('No patch to download');
      return;
    }
    downloadJson(patchResult, 'json-patch.json', 'Patch downloaded');
  };

  const diffJson = () => (visualDiff?.ok ? JSON.stringify(visualDiff.value, null, 2) : '');

  const handleCopyDiff = async () => {
    if (!visualDiff?.ok || visualDiff.value.length === 0) {
      setSnackbarMsg('No visual diff to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(diffJson());
      setSnackbarMsg('Diff copied to clipboard');
    } catch {
      setSnackbarMsg('Failed to copy diff.');
    }
  };

  const handleDownloadDiff = () => {
    if (!visualDiff?.ok || visualDiff.value.length === 0) {
      setSnackbarMsg('No visual diff to download');
      return;
    }
    downloadJson(diffJson(), 'json-patch-diff.json', 'Diff downloaded');
  };

  const handleReset = () => {
    setOldJson('');
    setNewJson('');
    setPatchResult('');
    setAppliedResult('');
    clearFeedback();

    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const closeSnackbar = () => setSnackbarMsg('');

  const renderToolbarButton = (
    title: string,
    Icon: PanelIconComponent,
    onClick: (() => void) | (() => Promise<void>)
  ) => (
    <Tooltip title={title} arrow>
      <IconButton
        size="small"
        onClick={onClick}
        className="editor-panel-icon-button"
        aria-label={title}
      >
        <Icon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  );

  const renderLabelWithBadge = (label: string, badge: string) => (
    <>
      {label}
      <Box
        component="span"
        sx={{
          px: 1,
          py: '2px',
          fontSize: '0.65rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          borderRadius: '9999px',
          backgroundColor: 'color-mix(in srgb, var(--accent) 14%, transparent)',
          color: 'var(--accent-text)'
        }}
      >
        {badge}
      </Box>
    </>
  );

  const renderDiffRows = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {visualDiff?.ok && visualDiff.value.map((item, index) => (
        <Box key={`${item.op}-${item.path}-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' }, gap: 1, alignItems: 'start' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>
            {item.op}
          </Typography>
          <Box>
            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'var(--fg)', wordBreak: 'break-word' }}>
              {item.from ? `${item.from} -> ${item.path}` : item.path}
            </Typography>
            {('before' in item || 'after' in item) && (
              <Typography sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--fg-muted)', wordBreak: 'break-word' }}>
                {'before' in item ? JSON.stringify(item.before) : ''}
                {'after' in item ? ` -> ${JSON.stringify(item.after)}` : ''}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  const visualDiffTone = (op: string) => {
    if (op === 'add' || op === 'copy') {
      return { label: 'Added', color: '#137333', bg: 'rgba(19, 115, 51, 0.08)', border: 'rgba(19, 115, 51, 0.28)' };
    }
    if (op === 'remove') {
      return { label: 'Removed', color: '#b3261e', bg: 'rgba(179, 38, 30, 0.08)', border: 'rgba(179, 38, 30, 0.28)' };
    }
    return { label: op === 'replace' ? 'Changed' : op, color: '#8a4b08', bg: 'rgba(180, 95, 6, 0.08)', border: 'rgba(180, 95, 6, 0.28)' };
  };

  const renderSemanticDiff = () => (
    <Box
      aria-label="Visual JSON diff"
      sx={{ mt: 3, p: { xs: 2, md: 3 }, border: '1px solid var(--border)', borderRadius: '12px', backgroundColor: 'var(--surface)' }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          <Typography variant="h2" sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--fg)' }}>
            Visual Diff
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
            Semantic changes derived from the RFC 6902 patch. Object key order is ignored.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowRawDiffPatch((visible) => !visible)}
          aria-expanded={showRawDiffPatch}
          className="pill-button-secondary"
        >
          {showRawDiffPatch ? 'Hide JSON Patch' : 'View JSON Patch'}
        </Button>
      </Box>

      {visualDiff?.ok && visualDiff.value.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {visualDiff.value.map((item, index) => {
            const tone = visualDiffTone(item.op);
            const showBefore = item.before !== undefined;
            const showAfter = item.after !== undefined;
            return (
              <Box
                key={`${item.op}-${item.path}-${index}`}
                sx={{ p: 2, border: `1px solid ${tone.border}`, borderLeft: `4px solid ${tone.color}`, borderRadius: '8px', backgroundColor: tone.bg }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: showBefore || showAfter ? 1.25 : 0 }}>
                  <Box component="span" sx={{ px: 1, py: '2px', borderRadius: '999px', color: tone.color, backgroundColor: 'var(--surface)', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {tone.label}
                  </Box>
                  <Typography component="code" sx={{ color: 'var(--fg)', fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: 700, wordBreak: 'break-all' }}>
                    {item.from ? `${item.from} → ${item.path}` : item.path || '/'}
                  </Typography>
                </Box>
                {(showBefore || showAfter) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: showBefore && showAfter ? { xs: '1fr', md: '1fr 1fr' } : '1fr', gap: 1 }}>
                    {showBefore && (
                      <Box sx={{ p: 1.25, borderRadius: '6px', backgroundColor: 'rgba(179, 38, 30, 0.07)', minWidth: 0 }}>
                        <Typography sx={{ color: '#b3261e', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', mb: 0.5 }}>Before</Typography>
                        <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', color: 'var(--fg)', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>{JSON.stringify(item.before, null, 2)}</Typography>
                      </Box>
                    )}
                    {showAfter && (
                      <Box sx={{ p: 1.25, borderRadius: '6px', backgroundColor: 'rgba(19, 115, 51, 0.07)', minWidth: 0 }}>
                        <Typography sx={{ color: '#137333', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', mb: 0.5 }}>After</Typography>
                        <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', color: 'var(--fg)', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>{JSON.stringify(item.after, null, 2)}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: '8px' }}>
          <Typography sx={{ color: 'var(--fg-muted)' }}>
            {patchResult.trim() ? 'No differences found.' : 'Compare two JSON documents to see their changes here.'}
          </Typography>
        </Box>
      )}

      {visualDiff?.ok && visualDiff.value.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
          <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyDiff} className="pill-button-secondary">Copy Diff</Button>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadDiff} className="pill-button-secondary">Download Diff</Button>
        </Box>
      )}
    </Box>
  );

  const renderPatchActionsRow = () => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', mt: 2, mb: 1, '& .MuiButton-root': { color: 'var(--accent-text)', borderColor: 'color-mix(in srgb, var(--accent-text) 40%, var(--border))' } }}>
      <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleCopyPatch} className="pill-button-secondary">
        Copy Patch
      </Button>
      <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadPatch} className="pill-button-secondary">
        Download Patch
      </Button>
      <Button variant="outlined" startIcon={<LinkIcon />} onClick={handleCopyLink} className="pill-button-secondary">
        Copy Share Link
      </Button>
    </Box>
  );

  const renderModeView = (viewMode: EditorMode) => {
    switch (viewMode) {
      case 'generate':
        return (
          <Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <EditorPanel
                  label="Source JSON"
                  value={oldJson}
                  onChange={updateOldJson}
                  onFileDrop={handleFileDrop(updateOldJson, 'Source JSON')}
                  onFileDropError={handleFileDropError}
                  placeholder="Paste your source JSON here, or drop a .json file"
                  onExpand={() => setExpandedEditor('old')}
                  isDarkMode={isDarkMode}
                  height={250}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditorPanel
                  label="Target JSON"
                  value={newJson}
                  onChange={updateNewJson}
                  onFileDrop={handleFileDrop(updateNewJson, 'Target JSON')}
                  onFileDropError={handleFileDropError}
                  placeholder="Paste your modified target JSON here, or drop a .json file"
                  onExpand={() => setExpandedEditor('new')}
                  toolbarActions={renderToolbarButton('Swap Source and Target', SwapHorizIcon, handleSwap)}
                  isDarkMode={isDarkMode}
                  height={250}
                />
              </Grid>
            </Grid>

            <ActionButtons
              mode="generate"
              onGenerate={handleGeneratePatch}
              onApply={handleApplyPatch}
              onVerify={handleVerifyPatch}
              onReset={handleReset}
              generateLabel={isDiffPage ? 'Generate Diff' : undefined}
              generateHint={isDiffPage ? 'Your semantic diff appears below. The RFC 6902 patch stays one tap away.' : undefined}
            />

            {feedback}

            {isDiffPage ? (
              <>
                {renderSemanticDiff()}
                {showRawDiffPatch && (
                  <Box ref={patchResultRef} sx={{ mt: 3, scrollMarginTop: '96px' }}>
                    <EditorPanel
                      label={renderLabelWithBadge('JSON Patch', 'RFC 6902')}
                      ariaLabel="JSON Patch"
                      value={patchResult}
                      onChange={updatePatchResult}
                      readOnly={!patchResult.trim()}
                      inactive={!patchResult.trim()}
                      inactiveHint="Generate a diff and the RFC 6902 patch will appear here"
                      onExpand={() => setExpandedEditor('patch')}
                      tinted={true}
                      isDarkMode={isDarkMode}
                      height={250}
                    />
                    {renderPatchActionsRow()}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box ref={patchResultRef} sx={{ scrollMarginTop: '96px' }}>
                  <EditorPanel
                    label={renderLabelWithBadge('JSON Patch', 'output')}
                    ariaLabel="JSON Patch"
                    value={patchResult}
                    onChange={updatePatchResult}
                    readOnly={!patchResult.trim()}
                    inactive={!patchResult.trim()}
                    inactiveHint="Generate a patch and it will appear here"
                    onExpand={() => setExpandedEditor('patch')}
                    tinted={true}
                    isDarkMode={isDarkMode}
                    height={250}
                  />
                </Box>

                {renderPatchActionsRow()}

                {visualDiff?.ok && visualDiff.value.length > 0 && (
                  <Box sx={{ mt: 3, p: 3, border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--surface)' }}>
                    <Typography variant="h2" sx={{ fontSize: '1rem', fontWeight: 700, mb: 2, color: 'var(--fg)' }}>
                      Visual Diff
                    </Typography>
                    {renderDiffRows()}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
                      <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={handleCopyDiff} className="pill-button-secondary">
                        Copy Diff
                      </Button>
                      <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadDiff} className="pill-button-secondary">
                        Download Diff
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        );
      case 'apply':
        return (
          <ApplyView
            oldJson={oldJson}
            updateOldJson={updateOldJson}
            handleFileDrop={handleFileDrop}
            handleFileDropError={handleFileDropError}
            setExpandedEditor={setExpandedEditor}
            isDarkMode={isDarkMode}
            patchResult={patchResult}
            updatePatchResult={updatePatchResult}
            renderLabelWithBadge={renderLabelWithBadge}
            renderToolbarButton={renderToolbarButton}
            handlePasteIntoPatch={handlePasteIntoPatch}
            renderPatchActionsRow={renderPatchActionsRow}
            handleGeneratePatch={handleGeneratePatch}
            handleApplyPatch={handleApplyPatch}
            handleVerifyPatch={handleVerifyPatch}
            handleReset={handleReset}
            showErrorBanner={Boolean(failureDetail?.headline)}
            errorBanner={errorBanner}
            feedback={feedback}
            appliedResult={appliedResult}
            appliedResultRef={appliedResultRef}
            setAppliedResult={setAppliedResult}
            handleCopyResult={handleCopyResult}
            handleUseResultAsSource={handleUseResultAsSource}
          />
        );
      case 'verify':
        return (
          <VerifyView
            oldJson={oldJson}
            updateOldJson={updateOldJson}
            newJson={newJson}
            updateNewJson={updateNewJson}
            handleFileDrop={handleFileDrop}
            handleFileDropError={handleFileDropError}
            setExpandedEditor={setExpandedEditor}
            handleSwap={handleSwap}
            isDarkMode={isDarkMode}
            patchResult={patchResult}
            updatePatchResult={updatePatchResult}
            renderLabelWithBadge={renderLabelWithBadge}
            renderToolbarButton={renderToolbarButton}
            handlePasteIntoPatch={handlePasteIntoPatch}
            renderPatchActionsRow={renderPatchActionsRow}
            handleGeneratePatch={handleGeneratePatch}
            handleApplyPatch={handleApplyPatch}
            handleVerifyPatch={handleVerifyPatch}
            handleReset={handleReset}
            verifyVerdict={verifyVerdict}
            showOperations={Boolean(statusMessage && visualDiff?.ok && visualDiff.value.length > 0)}
            verifyOpsRef={verifyOpsRef}
            renderDiffRows={renderDiffRows}
            handleCopyDiff={handleCopyDiff}
            handleDownloadDiff={handleDownloadDiff}
          />
        );
    }
  };

  const bannerSx = (isError: boolean) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    mt: 2,
    mb: 3,
    p: { xs: 2.5, sm: 3 },
    borderRadius: 'var(--radius-lg)',
    border: isError ? '1px solid var(--error)' : '1px solid var(--success)',
    backgroundColor: isError
      ? 'color-mix(in srgb, var(--error) 12%, var(--surface))'
      : 'color-mix(in srgb, var(--success) 12%, var(--surface))'
  });

  // Library failures lead with the humanized headline + suggestion; verify
  // mismatches keep their own headline and the plain-language diff detail.
  const errorDetailText = failureDetail?.headline
    ? failureDetail.hint || ''
    : errorMessage
        .replace(/^Verification failed\s*[-–—:]\s*/i, '')
        .replace(/^./, (c) => c.toUpperCase());

  const errorBanner = (
    <Box role="alert" sx={bannerSx(true)}>
      <CancelIcon sx={{ fontSize: 44, color: 'var(--error)', flexShrink: 0, mt: 0.25 }} />
      <Box sx={{ textAlign: 'left', minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.3, color: 'var(--fg)', wordBreak: 'break-word' }}>
          {failureDetail?.headline || 'Verification failed'}
        </Typography>
        {errorDetailText && (
          <Typography sx={{ mt: 0.5, fontSize: 'var(--font-size-base)', color: 'var(--fg-muted)', wordBreak: 'break-word' }}>
            {errorDetailText}
          </Typography>
        )}
        {failureDetail?.technical && (
          <Box component="details" sx={{ mt: 1.5 }}>
            <Box
              component="summary"
              sx={{
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--fg-muted)',
                '&:hover': { color: 'var(--fg)' }
              }}
            >
              Technical details
            </Box>
            <Box
              component="pre"
              sx={{
                mt: 1,
                mb: 0,
                p: 1.5,
                maxHeight: 220,
                overflow: 'auto',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-xs)',
                lineHeight: 1.5,
                color: 'var(--fg-muted)',
                backgroundColor: 'var(--bg-subtle)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {failureDetail.technical}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  const verifyVerdict = (statusMessage || errorMessage) && (
    errorMessage ? errorBanner : (
      <Box role="status" sx={bannerSx(false)}>
        <CheckCircleIcon sx={{ fontSize: 44, color: 'var(--success)', flexShrink: 0, mt: 0.25 }} />
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.3, color: 'var(--fg)' }}>
            Patch verified
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 'var(--font-size-base)', color: 'var(--fg-muted)', wordBreak: 'break-word' }}>
            Applying the patch to Source reproduces Target exactly.
          </Typography>
        </Box>
      </Box>
    )
  );

  const feedback = (
    <>
      {statusMessage && (
        <Typography role="status" variant="body2" sx={{ mb: 2, textAlign: 'center', color: '#16a34a', fontWeight: 600 }}>
          {statusMessage}
        </Typography>
      )}

      {errorMessage && !failureDetail?.headline && (
        <Typography role="alert" variant="body2" color="error" sx={{ mb: 2, textAlign: 'center', fontWeight: 600 }}>
          {errorMessage}
        </Typography>
      )}
    </>
  );

  let dialogTitle = '';
  let dialogValue = '';
  let dialogSetter: (val: string) => void = () => { };
  let dialogReadOnly = false;

  switch (expandedEditor) {
    case 'old':
      dialogTitle = 'Source JSON';
      dialogValue = oldJson;
      dialogSetter = updateOldJson;
      break;
    case 'new':
      dialogTitle = 'Target JSON';
      dialogValue = newJson;
      dialogSetter = updateNewJson;
      break;
    case 'patch':
      dialogTitle = 'JSON Patch';
      dialogValue = patchResult;
      dialogSetter = updatePatchResult;
      break;
  }

  return (
    <>
      <Navigation activePage={activePage} />

      <main>
        <Container maxWidth="lg" className="main-container">
          {hero ?? (
            <>
          <Typography variant="h1" gutterBottom className="hero-title">
              JSON Patch Generator
            </Typography>

            <Typography variant="h2" className="hero-subtitle">
              Pick what you want to do - generate an RFC 6902 JSON Patch from two documents, apply an existing one, or verify it round-trips. Everything runs directly in your browser.
            </Typography>

            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: 'var(--fg-muted)' }}>
              <Box component="strong" sx={{ color: 'var(--fg)' }}>Your JSON stays in your browser.</Box>{' '}
              Transformations run locally. Hosting and cookieless analytics requests still occur; share links put encoded editor state in the URL.
            </Typography>
            </>
          )}

          <Box>
            {!isDiffPage && <ModeTabs mode={mode} onChange={handleModeChange} />}

<Box sx={{ display: 'grid', overflowX: 'hidden' }}>
              {visibleModes.map((viewMode) => (
                <CSSTransition
                  key={viewMode}
                  nodeRef={modeNodeRefs[viewMode]}
                  in={viewMode === activeMode}
                  timeout={320}
                  classNames={`mode-${slideDir}`}
                  mountOnEnter
                  unmountOnExit
                >
                  <Box
                    ref={(el) => {
                      modeNodeRefs[viewMode].current = (el as HTMLElement | null) ?? undefined;
                    }}
                    sx={{ gridArea: '1 / 1', minWidth: 0 }}
                  >
                    {renderModeView(viewMode)}
                  </Box>
                </CSSTransition>
              ))}
            </Box>
          </Box>

          {children ?? (
            <>
          <Divider sx={{ mt: 10, mb: 0, borderColor: 'var(--border)' }} />

          <Paper elevation={0} sx={{ mt: 6, p: 3, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
            <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--fg)', mb: 1 }}>
              Tested round trip
            </Typography>
            <Typography sx={{ color: 'var(--fg-muted)', lineHeight: 1.7 }}>
              Verified 19 September 2026 with fast-json-patch 3.1.1: comparing <code>{'{ "version": 1 }'}</code> with <code>{'{ "version": 2 }'}</code> produced <code>{'{ "op": "replace", "path": "/version", "value": 2 }'}</code>, and applying it produced the expected document. A failing <code>test</code> returned <code>TEST_OPERATION_FAILED</code>.
            </Typography>
          </Paper>

          {/* Lazy loaded non-critical sections */}
          <Features />
          <LearningSection />
          <FAQ />

            </>
          )}

          <Snackbar
            open={Boolean(snackbarMsg)}
            autoHideDuration={3000}
            onClose={closeSnackbar}
            message={snackbarMsg}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            TransitionComponent={SlideTransition}
            ContentProps={{
              sx: {
                backgroundColor: 'var(--surface)',
                color: 'var(--fg)',
                backdropFilter: 'blur(20px)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border)',
                boxShadow: 'var(--shadow-lg)',
                '& .MuiSnackbarContent-message': {
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 500,
                  textAlign: 'center',
                  width: '100%'
                }
              }
            }}
          />
        </Container>
      </main>

      <ExpandDialog
        open={Boolean(expandedEditor)}
        title={dialogTitle}
        value={dialogValue}
        onClose={() => setExpandedEditor(null)}
        onChange={dialogSetter}
        readOnly={dialogReadOnly}
        isDarkMode={isDarkMode}
      />

      <Analytics />
      <SpeedInsights />
      <AnalyticsTracker />
    </>
  );
}

export default Editor;
